import type { PlanFormState } from './types';
import type { Instant, Period } from '@js-joda/core';
import type {
    ReplaceSubscriptionPlanRequest,
    Subscription,
    SubscriptionPlan,
    SubscriptionPlanWriteRequest,
} from '@thinkalpha/access-ws-client/contracts/subscription.js';
import type { BadgeFill } from 'src/components/ui/badges/badge';

type BadgeColor = 'green' | 'yellow' | 'red' | 'gray' | 'cyan';

export interface StatusBadgeProps {
    color: BadgeColor;
    fill: BadgeFill;
    label: string;
}

export const statusBadgeProps: Record<Subscription['status'], StatusBadgeProps> = {
    active: { color: 'green', fill: 'medium', label: 'Active' },
    trialing: { color: 'cyan', fill: 'medium', label: 'Trialing' },
    active_canceling: { color: 'yellow', fill: 'medium', label: 'Canceling' },
    active_plan_change_scheduled: { color: 'yellow', fill: 'medium', label: 'Plan Change Scheduled' },
    past_due: { color: 'red', fill: 'light', label: 'Past Due' },
    pending_payment_method: { color: 'yellow', fill: 'light', label: 'Pending Payment' },
    pending_activation: { color: 'yellow', fill: 'light', label: 'Pending Activation' },
    canceled: { color: 'red', fill: 'medium', label: 'Canceled' },
    aborted: { color: 'red', fill: 'medium', label: 'Aborted' },
    paused: { color: 'gray', fill: 'medium', label: 'Paused' },
};

export function formatDate(instant: Instant): string {
    try {
        return new Date(instant.toString()).toLocaleDateString();
    } catch {
        return instant.toString();
    }
}

export function formatPeriod(period: Period): string {
    const years = period.years();
    const months = period.months();
    const days = period.days();

    const parts: string[] = [];
    if (years === 1) parts.push('Year');
    else if (years > 1) parts.push(`${years} Years`);
    if (months === 1) parts.push('Month');
    else if (months > 1) parts.push(`${months} Months`);
    if (days === 1) parts.push('Day');
    else if (days > 1) parts.push(`${days} Days`);

    return parts.length > 0 ? `Per ${parts.join(', ')}` : period.toString();
}

export function formatPrice(price: number | false, currency: string): string {
    if (price === false) return 'Free';
    return `${price} ${currency}`;
}

export function planToFormState(plan: SubscriptionPlan): PlanFormState {
    return {
        name: plan.name,
        description: plan.description ?? '',
        price: plan.price === false ? '' : String(plan.price),
        professionalTraderPrice:
            plan.professionalTraderPrice === null || plan.professionalTraderPrice === false
                ? ''
                : String(plan.professionalTraderPrice),
        currency: plan.currency,
        billingInterval: plan.billingInterval.toString(),
        active: plan.active,
        selfServe: plan.selfServe,
        isAddOn: plan.isAddOn,
        inviteCodeRequired: plan.inviteCodeRequired,
        advertised: plan.advertised,
        maxPerUser: plan.maxPerUser !== null ? String(plan.maxPerUser) : '',
        features: plan.features ?? [],
        roles: (plan.roles ?? []).filter((r): r is string => typeof r === 'string'),
        groups: plan.groups.map((g) => ({ groupId: g.groupId, permission: g.permission })),
        badgeText: plan.badgeText ?? '',
        default: plan.default,
        existingSubscriptionBehavior: 'none',
    };
}

export function createDefaultFormState(): PlanFormState {
    return {
        name: '',
        description: '',
        price: '',
        professionalTraderPrice: '',
        currency: 'USD',
        billingInterval: 'P1M',
        active: false,
        selfServe: false,
        isAddOn: false,
        inviteCodeRequired: false,
        advertised: false,
        maxPerUser: '',
        features: [],
        roles: [],
        groups: [],
        badgeText: '',
        default: false,
        existingSubscriptionBehavior: 'none',
    };
}

/**
 * Build the full plan body the backend PUT/POST endpoints expect.
 *
 * The backend write contract is `.strict()` and requires every plan field, including
 * `introductoryPricing`, `includeFeaturesFromPlans`, and `replacesPlanIds`. The form
 * doesn't expose those, so when `original` is provided (edit flow) we pass them through
 * untouched, and when it's absent (create flow) we default them.
 */
export function formStateToWriteRequest(
    form: PlanFormState,
    original?: SubscriptionPlan,
): SubscriptionPlanWriteRequest {
    const formPrice = form.price === '' ? false : Number(form.price);
    // planToFormState collapses both `null` and `false` to '' for the pro-trader
    // price field, so a blank form value is ambiguous on save. Preserve the
    // original `false` ("plan not available to pro traders") when present,
    // otherwise default to `null` ("use retail price").
    const formPtPrice =
        form.professionalTraderPrice === ''
            ? original?.professionalTraderPrice === false
                ? false
                : null
            : Number(form.professionalTraderPrice);
    const formMaxPerUser = form.maxPerUser === '' ? null : Number(form.maxPerUser);
    const formFeatures = form.features.map((f) => f.trim()).filter(Boolean);

    return {
        name: form.name,
        description: form.description || null,
        price: formPrice,
        professionalTraderPrice: formPtPrice,
        currency: form.currency,
        // PeriodCodec serializes ISO-8601 period strings on the wire — the request
        // contract is the schema input shape, so pass the raw form string through
        // instead of parsing to a Period instance.
        billingInterval: form.billingInterval,
        active: form.active,
        selfServe: form.selfServe,
        isAddOn: form.isAddOn,
        inviteCodeRequired: form.inviteCodeRequired,
        advertised: form.advertised,
        maxPerUser: formMaxPerUser,
        features: formFeatures.length > 0 ? formFeatures : null,
        roles: form.roles,
        groups: form.groups,
        badgeText: form.badgeText || null,
        default: form.default,
        // Fields not exposed by the form — preserve from the original on edit; default on create.
        // The original's `period` is a parsed `Period` (Zod output) but the request takes
        // the schema input shape (string), so re-serialize before sending.
        introductoryPrice: original?.introductoryPrice
            ? { ...original.introductoryPrice, period: original.introductoryPrice.period.toString() }
            : null,
        introductoryPricing:
            original?.introductoryPricing.map((stage) => ({ ...stage, period: stage.period.toString() })) ?? [],
        includeFeaturesFromPlans: original?.includeFeaturesFromPlans ?? [],
        replacesPlanIds: original?.replacesPlanIds ?? [],
    };
}

export function formStateToReplaceRequest(
    form: PlanFormState,
    original: SubscriptionPlan,
): ReplaceSubscriptionPlanRequest {
    return {
        ...formStateToWriteRequest(form, original),
        existingSubscriptionBehavior: form.existingSubscriptionBehavior,
    };
}
